import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Check, CheckCircle2, MapPin, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MyAgendaItem, MyAgendaList, RsvpAnswer } from '@up2d8/contracts';
import { useState } from 'react';
import { Badge, Button, Card, EmptyState, LoadingState, cn, dayOf, useToast } from '@up2d8/ui';
import { minutesOnDay, monthsInRange, step, visibleDays } from '../../shared/calendar-range';
import {
  AgendaCalendar,
  CalendarToolbar,
  calendarTitle,
  rememberView,
  rememberedView,
  type AgendaView,
} from '../agenda/agenda-calendar';
import { DeskShell } from './desk/desk-shell';
import { useSession } from '../../shared/session';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';
import { uiLocale } from '../../shared/format';
import { ListError } from '../../shared/list-error';

/**
 * De agenda van de medewerker (Stap 25).
 *
 * Eerstvolgende bovenaan, en alleen vooruit in de tijd: een agenda is geen archief. Wat vandaag al
 * voorbij is, blijft één dag staan — wie 's avonds kijkt of de toolbox van vanmorgen afgevinkt is,
 * hoort hem nog te zien.
 *
 * Sinds FBK-9 ook als kalender (dag, week, maand), op vraag van de testers. De lijst blijft de
 * standaard: op een telefoon is "wat komt er" de eerste vraag. Onder de kalender staan de kaarten
 * van de gekozen dag, met hun knoppen om aan te melden.
 */
export function AppAgendaPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const tabs = useAppTabs();
  const [view, setViewState] = useState<AgendaView>(() => rememberedView());
  const [anchor, setAnchor] = useState(() => dayOf(new Date()));
  const setView = (next: AgendaView) => {
    setViewState(next);
    rememberView(next);
  };

  // De kalender vraagt per maand (`?month=`): elke maand die de weergave raakt. Het maandraster
  // loopt meestal over drie maanden; een week over twee maanden vraagt er twee.
  const months =
    view === 'list'
      ? []
      : (() => {
          const range = visibleDays(view, anchor);
          return monthsInRange(range.first, range.after);
        })();

  const agenda = useQuery({
    queryKey: ['my-agenda', months],
    queryFn: async (): Promise<MyAgendaItem[]> => {
      if (months.length === 0) {
        const { data, error } = await api.GET('/api/v1/me/agenda');
        if (error || !data) throw error ?? new Error('Ophalen mislukt');
        return (data as MyAgendaList).items;
      }
      const parts = await Promise.all(
        months.map(async (month) => {
          const { data, error } = await api.GET('/api/v1/me/agenda', {
            params: { query: { month } } as never,
          });
          if (error || !data) throw error ?? new Error('Ophalen mislukt');
          return (data as MyAgendaList).items;
        }),
      );
      const seen = new Set<string>();
      return parts.flat().filter((item) => !seen.has(item.id) && seen.add(item.id));
    },
  });

  const items = agenda.data ?? [];
  const onAnchorDay = items.filter(
    (item) => minutesOnDay(anchor, new Date(item.startsAt), new Date(item.endsAt)) !== null,
  );

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="agenda"
      renderLink={renderAppTabLink}
    >
      <h1 className="font-heading text-page-title font-semibold text-text-primary">
        {t('agenda.title')}
      </h1>

      <CalendarToolbar
        view={view}
        onView={setView}
        title={view === 'list' ? '' : calendarTitle(view, anchor, uiLocale())}
        onPrevious={() => view !== 'list' && setAnchor(step(view, anchor, -1))}
        onNext={() => view !== 'list' && setAnchor(step(view, anchor, 1))}
        onToday={() => setAnchor(dayOf(new Date()))}
      />

      {view !== 'list' ? (
        agenda.isError ? (
          <ListError onRetry={() => void agenda.refetch()} error={agenda.error} />
        ) : (
          <>
            <Card>
              <AgendaCalendar
                view={view}
                anchor={anchor}
                items={items}
                onPickDay={(day) => {
                  setAnchor(day);
                  if (view === 'month') return;
                  setView('day');
                }}
                onOpenItem={(item) => setAnchor(dayOf(new Date(item.startsAt)))}
              />
            </Card>
            <h2 className="font-heading text-card-title font-semibold text-text-primary">
              {calendarTitle('day', anchor, uiLocale())}
            </h2>
            {onAnchorDay.length === 0 ? (
              <p className="text-small text-text-secondary">{t('agendaCalendar.nothingThatDay')}</p>
            ) : (
              <div className="flex flex-col gap-element">
                {onAnchorDay.map((item) => (
                  <AgendaCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </>
        )
      ) : agenda.isPending ? (
        <LoadingState rows={3} label={t('common.loading')} />
      ) : agenda.isError ? (
        // Niet de lege toestand: "er staat niets gepland" en "we weten het niet" zijn niet hetzelfde.
        <ListError onRetry={() => void agenda.refetch()} error={agenda.error} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState title={t('agenda.empty')} description={t('agenda.emptyHint')} />
        </Card>
      ) : (
        <div className="flex flex-col gap-element">
          {items.map((item) => (
            <AgendaCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </DeskShell>
  );
}

function AgendaCard({ item }: { item: MyAgendaItem }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const respond = useMutation({
    mutationFn: async (answer: RsvpAnswer) => {
      const { error } = await api.PUT('/api/v1/me/agenda/{itemId}/rsvp', {
        params: { path: { itemId: item.id } },
        body: { answer },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-agenda'] });
      // Een beantwoord evenement staat niet meer bij "Te doen".
      void queryClient.invalidateQueries({ queryKey: ['open-items'] });
    },
    onError: () => {
      // Geen optimistische update hier, anders dan bij een like: een antwoord op een toolbox is
      // iets waar een planner op rekent, en dan hoort de medewerker te zien dat het aankwam.
      toast.show({ tone: 'danger', title: t('agenda.rsvpFailed') });
    },
  });

  const start = new Date(item.startsAt);
  const end = new Date(item.endsAt);
  const cancelled = item.status === 'cancelled';
  // Dezelfde grens als de server (`rsvpOpen`): na de deadline, of zonder deadline na de start,
  // weigert hij elk antwoord. Het gegeven antwoord blijft zichtbaar, de knoppen niet meer bruikbaar.
  const rsvpClosed = Date.now() > new Date(item.rsvpDeadline ?? item.startsAt).getTime();

  return (
    <Card data-testid="agenda-card" className={cn(cancelled && 'opacity-60')}>
      <div className="flex items-start justify-between gap-element">
        <div className="min-w-0">
          <p className="font-heading text-small font-semibold uppercase tracking-wide text-brand-primary">
            {t(`agenda.kind.${item.kind}`)}
          </p>
          <h2 className="mt-0.5 font-heading text-title-md font-semibold text-text-primary">
            {item.title}
          </h2>
        </div>
        {cancelled ? (
          <Badge tone="danger">{t('agenda.cancelled')}</Badge>
        ) : item.attended ? (
          // Wie afgevinkt is, hoort dat te kunnen zien: het is een vaststelling over hem.
          <Badge tone="success">
            <CheckCircle2 aria-hidden className="mr-1 inline h-3.5 w-3.5" />
            {t('agenda.attended')}
          </Badge>
        ) : null}
      </div>

      <dl className="mt-element flex flex-col gap-1 text-content text-text-secondary">
        <div className="flex items-center gap-2">
          <CalendarDays aria-hidden className="h-4 w-4 shrink-0 text-text-muted" />
          <dd>
            {start.toLocaleString(uiLocale(), {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              hour: '2-digit',
              minute: '2-digit',
            })}
            {' – '}
            {end.toLocaleTimeString(uiLocale(), { hour: '2-digit', minute: '2-digit' })}
          </dd>
        </div>
        {item.location && (
          <div className="flex items-center gap-2">
            <MapPin aria-hidden className="h-4 w-4 shrink-0 text-text-muted" />
            <dd>{item.location}</dd>
          </div>
        )}
      </dl>

      {item.rsvpEnabled && !cancelled && (
        <div className="mt-element flex flex-wrap items-center gap-element border-t border-border-light pt-element">
          <p className="text-small text-text-muted">{t('agenda.comingQuestion')}</p>
          <div className="flex gap-2">
            <Button
              variant={item.myAnswer === 'yes' ? 'primary' : 'secondary'}
              data-testid="rsvp-yes"
              aria-pressed={item.myAnswer === 'yes'}
              loading={respond.isPending && respond.variables === 'yes'}
              disabled={rsvpClosed}
              onClick={() => respond.mutate('yes')}
            >
              <Check aria-hidden className="mr-1 inline h-4 w-4" />
              {t('agenda.yes')}
            </Button>
            <Button
              variant={item.myAnswer === 'no' ? 'primary' : 'secondary'}
              data-testid="rsvp-no"
              aria-pressed={item.myAnswer === 'no'}
              loading={respond.isPending && respond.variables === 'no'}
              disabled={rsvpClosed}
              onClick={() => respond.mutate('no')}
            >
              <X aria-hidden className="mr-1 inline h-4 w-4" />
              {t('agenda.no')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
